import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MessageCircle, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Header = ({ whatsappUrl }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: t('nav.services'), href: '#services' },
    { name: t('nav.portfolio'), href: '#portfolio' },
    { name: t('nav.whyUs'), href: '#why-us' },
    { name: t('nav.contact'), href: '#contact' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/90 backdrop-blur-xl shadow-[0_1px_20px_-4px_rgba(0,0,0,0.08)] border-b border-gray-100' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="#"
            className="font-heading text-2xl font-black tracking-tight text-[#0A0A0A]"
            whileHover={{ scale: 1.02 }}
            data-testid="logo"
          >
            Sente<span className="text-[#E60000]">tica</span>
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <motion.a
                key={link.name}
                href={link.href}
                className="text-[#0A0A0A] font-medium text-sm tracking-wide hover:text-[#E60000] transition-colors duration-300 relative group"
                whileHover={{ y: -2 }}
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#E60000] transition-all duration-300 group-hover:w-full" />
              </motion.a>
            ))}
          </nav>

          {/* Right side: Language Switcher + CTA */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1" data-testid="language-switcher">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-300 ${
                  language === 'en' 
                    ? 'bg-[#E60000] text-white' 
                    : 'text-[#6B7280] hover:text-[#0A0A0A]'
                }`}
                data-testid="lang-en-btn"
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('tr')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all duration-300 ${
                  language === 'tr' 
                    ? 'bg-[#E60000] text-white' 
                    : 'text-[#6B7280] hover:text-[#0A0A0A]'
                }`}
                data-testid="lang-tr-btn"
              >
                TR
              </button>
            </div>

            {/* CTA Button */}
            <motion.a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E60000] text-white px-6 py-3 rounded-full font-bold text-sm shadow-lg hover:bg-[#CC0000] hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="header-whatsapp-btn"
            >
              <MessageCircle size={18} />
              {t('nav.getStarted')}
            </motion.a>
          </div>

          {/* Mobile: Language + Menu Button */}
          <div className="md:hidden flex items-center gap-3">
            {/* Mobile Language Switcher */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-0.5">
              <button
                onClick={() => setLanguage('en')}
                className={`px-2 py-1 rounded-full text-xs font-bold transition-all ${
                  language === 'en' 
                    ? 'bg-[#E60000] text-white' 
                    : 'text-[#6B7280]'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('tr')}
                className={`px-2 py-1 rounded-full text-xs font-bold transition-all ${
                  language === 'tr' 
                    ? 'bg-[#E60000] text-white' 
                    : 'text-[#6B7280]'
                }`}
              >
                TR
              </button>
            </div>

            <button
              className="p-2 text-[#0A0A0A]"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block text-[#0A0A0A] font-medium py-2 hover:text-[#E60000] transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </a>
              ))}
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#E60000] text-white px-6 py-3 rounded-full font-bold text-sm w-full mt-4"
                data-testid="mobile-whatsapp-btn"
              >
                <MessageCircle size={18} />
                {t('nav.getStarted')}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
